"use client";
import {
  cover24Hours,
  IResource,
  Office,
  parseDateOnlyString,
  parseTimeOnly,
  prettyTimeOnly
} from "@gettactic/api";
import { useDisclosure } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Clock, Pencil, Plus, Trash2 } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { CreateOrEditHourRule } from "@/features/settings/components/hours/rule/CreateOrEditHourRule";
import {
  groupSharedKey,
  IHourWithResName
} from "@/features/settings/components/hours/rule/ListHoursRules";
import { useBulkHourRules } from "@/features/settings/hooks/useHours";
import { api } from "@/lib/api/api";

/**
 * Custom-hours tab for the builder resource editor. Same data and flows
 * as the legacy HoursPanel/ListHourRules (shared rules grouped across the
 * selected resources, add/edit through CreateOrEditHourRule), but as a
 * compact card list that fits the side panel instead of a settings table.
 */
export const BuilderResourceHours: React.FC<{
  office: Office;
  resources: IResource[];
}> = ({ office, resources }) => {
  const queryClient = useQueryClient();
  const formDisclosure = useDisclosure<IHourWithResName[]>();
  const hours = useBulkHourRules(office, resources);

  const invalidateQuery = React.useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: ["hours.rules", office.id] }),
    [office.id, queryClient]
  );

  // One card per shared rule group (same grouping the legacy list used
  // for its edit/delete actions).
  const groups = React.useMemo(() => {
    const byKey = new Map<string, IHourWithResName[]>();
    for (const page of hours.data ?? []) {
      if (!page) continue;
      for (const hour of page.elements) {
        const res = resources.find((x) => x.id === hour.resource_id);
        const withName: IHourWithResName = {
          ...hour,
          resName: res?.name ?? "-"
        };
        const key = groupSharedKey(hour);
        const list = byKey.get(key) || [];
        list.push(withName);
        byKey.set(key, list);
      }
    }
    return [...byKey.values()];
  }, [hours.data, resources]);

  const handleDelete = async (group: IHourWithResName[]) => {
    try {
      await Promise.all(
        group.map((h) => api.client.hours.delete(office.id, h.id))
      );
      toast.success("Hours deleted");
      invalidateQuery();
    } catch {
      toast.error("Failed to delete hours");
    }
  };

  const formatDates = (hour: IHourWithResName) => {
    const since = hour.since ? parseDateOnlyString(hour.since) : null;
    const until = hour.until ? parseDateOnlyString(hour.until) : null;
    if (!since && !until) return "Always";
    const sinceShort = since ? format(since, "M/dd/yy") : null;
    const untilShort = until ? format(until, "M/dd/yy") : null;
    return sinceShort === untilShort
      ? `${sinceShort}`
      : `${sinceShort} – ${untilShort}`;
  };

  const formatHours = (hour: IHourWithResName) =>
    cover24Hours(parseTimeOnly(hour.start_time), parseTimeOnly(hour.end_time))
      ? "All day"
      : `${prettyTimeOnly(hour.start_time)} – ${prettyTimeOnly(hour.end_time)}`;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex flex-none items-center justify-between">
        <p className="font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
          Custom Hours
        </p>
        <button
          type="button"
          onClick={() => formDisclosure.open()}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 font-medium text-neutral-700 text-xs hover:bg-neutral-50"
        >
          <Plus size={12} /> Add Hours
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {hours.isLoading ? (
          <p className="py-8 text-center text-neutral-400 text-xs">
            Loading hours…
          </p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <Clock size={20} className="text-neutral-300" />
            <p className="font-medium text-neutral-700 text-sm">
              No custom hours
            </p>
            <p className="text-neutral-400 text-xs">
              These resources follow the office hours. Add rules for exceptions
              like closures or extended availability.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {groups.map((group) => {
              const hour = group[0];
              return (
                <div
                  key={hour.id}
                  className="group rounded-md border border-neutral-200 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium text-neutral-800 text-sm">
                        {hour.name || "Untitled rule"}
                      </span>
                      <span
                        className={cn(
                          "flex-none rounded-full px-2 py-0.5 font-medium text-[10px]",
                          hour.is_available
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        )}
                      >
                        {hour.is_available ? "Open" : "Closed"}
                      </span>
                    </div>
                    <div className="flex flex-none items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => formDisclosure.open(group)}
                        className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                        title="Edit hours"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(group)}
                        className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                        title="Delete hours"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-0.5 text-[11px] text-neutral-500">
                    {formatDates(hour)} · {formatHours(hour)}
                    {resources.length > 1 && (
                      <span className="text-neutral-400">
                        {" "}
                        · {group.length}/{resources.length} resources
                      </span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateOrEditHourRule
        hours={
          Array.isArray(formDisclosure.data) ? formDisclosure.data : undefined
        }
        isOpen={formDisclosure.isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) formDisclosure.close();
        }}
        office={office}
        resources={resources}
        invalidateQuery={invalidateQuery}
      />
    </div>
  );
};
